import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import Link from 'components/Basic/Link/Link';
import Overlay from 'components/Basic/Overlay/Overlay';
import Button from 'components/Forms/Button/Button';
import { Popup } from 'components/Layout/Popup/Popup';
import { Webline } from 'components/Layout/Webline/Webline';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { isB2bDomain, isOneOfDomainLocales } from 'helpers/isDomainLocale';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

const TEST_IDENTIFIER = 'blocks-userconsent-simplified';

type UserConsentSimplifiedProps = {
    cookiePolicyUrl: string;
    onAcceptAllCallback: () => void;
    onRejectAllCallback: () => void;
    onShowUserConsentPopup: () => void;
};

export const UserConsentSimplified: FC<UserConsentSimplifiedProps> = ({
    cookiePolicyUrl,
    onShowUserConsentPopup,
    onRejectAllCallback,
    onAcceptAllCallback,
}) => {
    const { t } = useTranslation();
    const domainConfig = useDomainConfig();
    const displayBottom =
        isOneOfDomainLocales(domainConfig.defaultLocale, ['pl', 'de-AT']) || isB2bDomain(domainConfig);

    return (
        <>
            {displayBottom ? (
                <>
                    <div
                        className="fixed inset-0 top-auto z-[801] w-full bg-white py-4 leading-6 vl:py-5"
                        data-testid={TEST_IDENTIFIER}
                    >
                        <Webline>
                            <div className="vl:flex vl:justify-between">
                                <div>
                                    <h5>{t('User consent preview title')}</h5>
                                    <p>
                                        <Trans
                                            i18nKey="User consent preview text"
                                            components={{
                                                link: (
                                                    <ExtendedNextLink
                                                        className="text-primary hover:text-grayDark"
                                                        href={cookiePolicyUrl || '#'}
                                                        rel="noreferrer"
                                                        target="_blank"
                                                    />
                                                ),
                                            }}
                                        />
                                    </p>
                                </div>
                                <div className="flex shrink-0 flex-col gap-2 pt-2 vl:ml-20 vl:w-60 vl:gap-5">
                                    <Button
                                        className="uppercase shadow-md"
                                        data-testid={TEST_IDENTIFIER + '-preferences'}
                                        size="small"
                                        type="button"
                                        onClick={onShowUserConsentPopup}
                                    >
                                        {t('Preferences')}
                                    </Button>
                                    <Button
                                        className="uppercase shadow-md"
                                        data-testid={TEST_IDENTIFIER + '-reject'}
                                        size="small"
                                        type="button"
                                        onClick={onRejectAllCallback}
                                    >
                                        {t('Reject all')}
                                    </Button>
                                    <Button
                                        className="uppercase shadow-md"
                                        data-testid={TEST_IDENTIFIER + '-accept-all'}
                                        type="button"
                                        variant="primary"
                                        onClick={onAcceptAllCallback}
                                    >
                                        <strong>{t('Accept all')}</strong>
                                    </Button>
                                </div>
                            </div>
                        </Webline>
                    </div>
                    <Overlay className="z-consentPopup" withWrapper={false} />
                </>
            ) : (
                <Popup
                    hideCloseButton
                    className="w-[600px] rounded-md px-5 py-10 text-center vl:w-[800px] vl:px-12"
                    contentClassName="flex flex-col gap-7 items-center sm:gap-12"
                >
                    <div className="h-full overflow-y-auto">
                        <div className="flex flex-col gap-5">
                            <h3 className="mb-0">{t('User consent preview title')}</h3>
                            <p className="mb-0">
                                <Trans
                                    i18nKey="User consent preview text"
                                    components={{
                                        link: (
                                            <Link
                                                className="inline"
                                                href={cookiePolicyUrl || '#'}
                                                rel="noreferrer"
                                                target="_blank"
                                            />
                                        ),
                                    }}
                                />
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-5 text-center sm:flex-row sm:gap-20">
                        <Button
                            data-testid={TEST_IDENTIFIER + '-preferences'}
                            size="small"
                            type="button"
                            onClick={onShowUserConsentPopup}
                        >
                            {t('Preferences')}
                        </Button>
                        <Button
                            data-testid={TEST_IDENTIFIER + '-accept-all'}
                            type="button"
                            variant="primary"
                            onClick={onAcceptAllCallback}
                        >
                            <strong>{t('Accept all')}</strong>
                        </Button>
                    </div>
                    <span>
                        <Trans
                            i18nKey="Cookie consent reject"
                            components={{
                                button: (
                                    <Button
                                        isLink
                                        data-testid={TEST_IDENTIFIER + '-reject'}
                                        type="button"
                                        onClick={onRejectAllCallback}
                                    />
                                ),
                            }}
                        />
                    </span>
                </Popup>
            )}
        </>
    );
};
